import { cellText, columnTitle } from './describe'
import { PALETTE, seriesColumns } from './display'
import type { Locale, ResultColumn, RunResult } from './types'

/**
 * GRAFICELE CONSTRUCTORULUI COMPLET, SVG scris aici, fara biblioteca: bare pe
 * mai multe masuri (cu perioada dinainte ca bara stearsa), bare stivuite pe
 * doua grupari, linie/suprafata pe mai multe serii, tabel incrucisat cu
 * umbrire (harta de caldura) si indicatorul cu tinta.
 *
 * Coloanele adaugate de motor (`share`, `previous`, `change`) nu sunt serii:
 * ponderea si schimbarea raman in tabel, iar perioada dinainte se deseneaza
 * stearsa langa prima masura.
 *
 * `onPick(i)` = omul a apasat randul `i` al rezultatului (o bara, un punct,
 * o celula): ecranul arata randurile din spatele lui.
 */

type Pick = ((row: number) => void) | undefined

function blank(locale: Locale): string {
  return locale === 'ro' ? '(gol)' : '(empty)'
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function short(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

/** Bare orizontale pe o grupare: o bara pe masura, plus perioada dinainte, stearsa. */
export function MultiBars({ result, locale, onPick }: { result: RunResult; locale: Locale; onPick?: Pick }) {
  const group = result.columns.find((c) => c.role === 'group')!
  const gi = result.columns.indexOf(group)
  const series = seriesColumns(result).slice(0, 4)
  const previous = result.columns.find((c) => c.agg === 'previous')
  const pi = previous ? result.columns.indexOf(previous) : -1
  const rows = result.rows.slice(0, 25)
  const lanes = series.length + (previous ? 1 : 0)
  const lane = lanes > 1 ? 9 : 16
  const rowH = Math.max(22, lanes * (lane + 2) + 6)
  const max = series.map((s, k) => {
    const i = result.columns.indexOf(s)
    const values = rows.map((r) => num(r.values[i]))
    if (k === 0 && pi >= 0) values.push(...rows.map((r) => num(r.values[pi])))
    return Math.max(1, ...values)
  })
  const height = rows.length * rowH + 6 + (lanes > 1 ? 22 : 0)
  return (
    <div className="od-rep-chart">
      {lanes > 1 ? (
        <ul className="od-rep-legend od-rep-legend-inline">
          {series.map((s, k) => (
            <li key={s.key}>
              <span className="od-rep-swatch" style={{ background: PALETTE[k % PALETTE.length] }} />
              {columnTitle(s, locale)}
            </li>
          ))}
          {previous ? (
            <li>
              <span className="od-rep-swatch od-rep-swatch-prev" />
              {columnTitle(previous, locale)}
            </li>
          ) : null}
        </ul>
      ) : null}
      <svg className="od-rep-bars" width="100%" height={height - (lanes > 1 ? 22 : 0)} role="img" aria-label={series.map((s) => columnTitle(s, locale)).join(', ')}>
        {rows.map((r, ri) => {
          const label = cellText(r.values[gi] ?? null, group, locale) || blank(locale)
          const bars = series.map((s, k) => ({ v: num(r.values[result.columns.indexOf(s)]), max: max[k]!, color: PALETTE[k % PALETTE.length]!, col: s, raw: r.values[result.columns.indexOf(s)] ?? null, prev: false }))
          if (previous) bars.splice(1, 0, { v: num(r.values[pi]), max: max[0]!, color: '#c7d3e0', col: previous, raw: r.values[pi] ?? null, prev: true })
          return (
            <g
              key={ri}
              transform={`translate(0, ${ri * rowH + 3})`}
              className={onPick ? 'od-rep-pick' : undefined}
              onClick={onPick ? () => onPick(ri) : undefined}
              role={onPick ? 'button' : undefined}
              tabIndex={onPick ? 0 : undefined}
              onKeyDown={onPick ? (e) => (e.key === 'Enter' || e.key === ' ') && onPick(ri) : undefined}
            >
              <title>{label}</title>
              <text x="0" y={Math.min(14, rowH / 2 + 4)} className="od-rep-bar-label">
                {short(label, 28)}
              </text>
              {bars.map((b, k) => {
                const pct = Math.max(0, b.v) / b.max
                const y = 2 + k * (lane + 2)
                return (
                  <g key={k}>
                    <rect x="34%" y={y} height={lane} width={`${(pct * 52).toFixed(2)}%`} rx="2" fill={b.color} className={k === 0 ? 'od-rep-bar' : undefined} />
                    <text x={`${(35 + pct * 52).toFixed(2)}%`} y={y + lane - 2} className={`od-rep-bar-value${b.prev ? ' od-rep-bar-prev' : ''}`}>
                      {cellText(b.raw, b.col, locale)}
                    </text>
                  </g>
                )
              })}
            </g>
          )
        })}
      </svg>
    </div>
  )
}

/** Doua grupari: o bara pe valoarea primei, felii pe valorile celei de-a doua (primele 7, restul "altele"). */
export function StackedBars({ result, locale, onPick }: { result: RunResult; locale: Locale; onPick?: Pick }) {
  const [g0, g1] = result.columns.filter((c) => c.role === 'group') as [ResultColumn, ResultColumn]
  const measure = seriesColumns(result)[0]!
  const i0 = result.columns.indexOf(g0)
  const i1 = result.columns.indexOf(g1)
  const mi = result.columns.indexOf(measure)
  const other = locale === 'ro' ? 'altele' : 'other'
  const totals = new Map<string, number>()
  for (const r of result.rows) {
    const k = cellText(r.values[i1] ?? null, g1, locale) || blank(locale)
    totals.set(k, (totals.get(k) ?? 0) + Math.max(0, num(r.values[mi])))
  }
  const keys = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k)
  const named = keys.slice(0, 7)
  const segOf = (k: string) => (named.includes(k) ? k : other)
  const segments = keys.length > 7 ? [...named, other] : named
  const lines: { label: string; parts: { seg: string; v: number; row: number }[]; total: number }[] = []
  result.rows.forEach((r, ri) => {
    const label = cellText(r.values[i0] ?? null, g0, locale) || blank(locale)
    let line = lines.find((l) => l.label === label)
    if (!line) {
      line = { label, parts: [], total: 0 }
      lines.push(line)
    }
    const v = Math.max(0, num(r.values[mi]))
    line.parts.push({ seg: segOf(cellText(r.values[i1] ?? null, g1, locale) || blank(locale)), v, row: ri })
    line.total += v
  })
  const shown = lines.slice(0, 25)
  const max = Math.max(1, ...shown.map((l) => l.total))
  const rowH = 24
  return (
    <div className="od-rep-chart">
      <ul className="od-rep-legend od-rep-legend-inline">
        {segments.map((s, k) => (
          <li key={s}>
            <span className="od-rep-swatch" style={{ background: PALETTE[k % PALETTE.length] }} />
            {s}
          </li>
        ))}
      </ul>
      <svg className="od-rep-bars" width="100%" height={shown.length * rowH + 6} role="img" aria-label={`${columnTitle(measure, locale)}: ${g0.label[locale]} / ${g1.label[locale]}`}>
        {shown.map((l, li) => {
          let x = 34
          return (
            <g key={l.label} transform={`translate(0, ${li * rowH + 3})`}>
              <text x="0" y="14" className="od-rep-bar-label">
                {short(l.label, 28)}
              </text>
              {l.parts.map((p, k) => {
                const w = (p.v / max) * 52
                const at = x
                x += w
                const color = PALETTE[segments.indexOf(p.seg) % PALETTE.length]
                return (
                  <rect
                    key={k}
                    x={`${at.toFixed(2)}%`}
                    y="2"
                    height="16"
                    width={`${Math.max(0, w).toFixed(2)}%`}
                    fill={color}
                    stroke="#fff"
                    strokeWidth="1"
                    className={onPick ? 'od-rep-pick' : undefined}
                    onClick={onPick ? () => onPick(p.row) : undefined}
                  >
                    <title>{`${l.label} · ${p.seg}: ${cellText(result.rows[p.row]!.values[mi] ?? null, measure, locale)}`}</title>
                  </rect>
                )
              })}
              <text x={`${(x + 1).toFixed(2)}%`} y="14" className="od-rep-bar-value">
                {cellText(l.total, measure, locale)}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

/**
 * Linie sau suprafata pe mai multe serii: pe doua grupari, o serie pe
 * valoarea celei de-a doua (primele 6); altfel o serie pe masura, plus
 * perioada dinainte, punctata.
 */
export function SeriesChart({ result, locale, area, onPick }: { result: RunResult; locale: Locale; area: boolean; onPick?: Pick }) {
  const groups = result.columns.filter((c) => c.role === 'group')
  const g0 = groups[0]!
  const i0 = result.columns.indexOf(g0)
  const measures = seriesColumns(result)
  const xs: string[] = []
  const rowAt = new Map<string, number>()
  type Series = { name: string; color: string; dashed: boolean; points: Map<string, number> }
  const series: Series[] = []
  const keyOf = (ri: number) => cellText(result.rows[ri]!.values[i0] ?? null, g0, locale) || blank(locale)
  result.rows.forEach((_, ri) => {
    const k = keyOf(ri)
    if (!xs.includes(k)) xs.push(k)
    if (!rowAt.has(k)) rowAt.set(k, ri)
  })
  if (groups.length >= 2) {
    const g1 = groups[1]!
    const i1 = result.columns.indexOf(g1)
    const mi = result.columns.indexOf(measures[0]!)
    const sums = new Map<string, number>()
    result.rows.forEach((r) => {
      const n = cellText(r.values[i1] ?? null, g1, locale) || blank(locale)
      sums.set(n, (sums.get(n) ?? 0) + num(r.values[mi]))
    })
    const names = [...sums.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6).map(([n]) => n)
    names.forEach((n, k) => series.push({ name: n, color: PALETTE[k % PALETTE.length]!, dashed: false, points: new Map() }))
    result.rows.forEach((r, ri) => {
      const s = series.find((x) => x.name === (cellText(r.values[i1] ?? null, g1, locale) || blank(locale)))
      if (s) s.points.set(keyOf(ri), num(r.values[mi]))
    })
  } else {
    measures.slice(0, 4).forEach((m, k) => {
      const mi = result.columns.indexOf(m)
      series.push({ name: columnTitle(m, locale), color: PALETTE[k % PALETTE.length]!, dashed: false, points: new Map(result.rows.map((r, ri) => [keyOf(ri), num(r.values[mi])])) })
    })
    const prev = result.columns.find((c) => c.agg === 'previous')
    if (prev) {
      const pi = result.columns.indexOf(prev)
      series.push({ name: columnTitle(prev, locale), color: '#8a99ab', dashed: true, points: new Map(result.rows.map((r, ri) => [keyOf(ri), num(r.values[pi])])) })
    }
  }
  const W = 640
  const H = 220
  const pad = { l: 46, r: 12, t: 12, b: 28 }
  const max = Math.max(1, ...series.flatMap((s) => [...s.points.values()]))
  const x = (i: number) => pad.l + (xs.length <= 1 ? (W - pad.l - pad.r) / 2 : (i / (xs.length - 1)) * (W - pad.l - pad.r))
  const y = (v: number) => H - pad.b - (Math.max(0, v) / max) * (H - pad.t - pad.b)
  const step = Math.max(1, Math.ceil(xs.length / 8))
  const first = measures[0]!
  return (
    <div className="od-rep-chart">
      {series.length > 1 ? (
        <ul className="od-rep-legend od-rep-legend-inline">
          {series.map((s) => (
            <li key={s.name}>
              <span className={`od-rep-swatch${s.dashed ? ' od-rep-swatch-prev' : ''}`} style={s.dashed ? undefined : { background: s.color }} />
              {s.name}
            </li>
          ))}
        </ul>
      ) : null}
      <svg className="od-rep-series" viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={series.map((s) => s.name).join(', ')}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={pad.l} x2={W - pad.r} y1={y(max * f)} y2={y(max * f)} stroke="#e8ecf1" />
            <text x={pad.l - 6} y={y(max * f) + 4} textAnchor="end" className="od-rep-axis">
              {cellText(Math.round(max * f * 100) / 100, first, locale)}
            </text>
          </g>
        ))}
        {xs.map((k, i) =>
          i % step === 0 ? (
            <text key={k} x={x(i)} y={H - 8} textAnchor="middle" className="od-rep-axis">
              {short(k, 12)}
            </text>
          ) : null
        )}
        {series.map((s) => {
          const pts = xs.map((k, i) => [x(i), y(s.points.get(k) ?? 0)] as const)
          const d = pts.map(([px, py], i) => `${i === 0 ? 'M' : 'L'}${px.toFixed(1)} ${py.toFixed(1)}`).join(' ')
          return (
            <g key={s.name}>
              {area && !s.dashed && pts.length > 1 ? <path d={`${d} L${pts[pts.length - 1]![0].toFixed(1)} ${H - pad.b} L${pts[0]![0].toFixed(1)} ${H - pad.b} Z`} fill={s.color} opacity={series.length > 1 ? 0.12 : 0.2} /> : null}
              <path d={d} fill="none" stroke={s.color} strokeWidth="2" strokeDasharray={s.dashed ? '5 4' : undefined} />
              {xs.length <= 40
                ? pts.map(([px, py], i) => (
                    <circle
                      key={i}
                      cx={px}
                      cy={py}
                      r="3"
                      fill="#fff"
                      stroke={s.color}
                      strokeWidth="1.5"
                      className={onPick && !s.dashed ? 'od-rep-pick' : undefined}
                      onClick={onPick && !s.dashed && groups.length === 1 ? () => onPick(rowAt.get(xs[i]!) ?? i) : undefined}
                    >
                      <title>{`${xs[i]} · ${s.name}: ${cellText(s.points.get(xs[i]!) ?? 0, first, locale)}`}</title>
                    </circle>
                  ))
                : null}
            </g>
          )
        })}
      </svg>
    </div>
  )
}

/**
 * Tabelul incrucisat: prima grupare pe randuri, a doua pe coloane, prima
 * masura in celule, umbrite dupa marime (harta de caldura). Pe o singura
 * grupare: o coloana, umbrita la fel.
 */
export function PivotTable({ result, locale, onPick }: { result: RunResult; locale: Locale; onPick?: Pick }) {
  const groups = result.columns.filter((c) => c.role === 'group')
  const g0 = groups[0]!
  const g1 = groups[1]
  const measure = seriesColumns(result)[0]!
  const i0 = result.columns.indexOf(g0)
  const i1 = g1 ? result.columns.indexOf(g1) : -1
  const mi = result.columns.indexOf(measure)
  const rowsK: string[] = []
  const colsK: string[] = []
  const cell = new Map<string, number>()
  let max = 0
  result.rows.forEach((r, ri) => {
    const a = cellText(r.values[i0] ?? null, g0, locale) || blank(locale)
    const b = g1 ? cellText(r.values[i1] ?? null, g1, locale) || blank(locale) : columnTitle(measure, locale)
    if (!rowsK.includes(a)) rowsK.push(a)
    if (!colsK.includes(b)) colsK.push(b)
    cell.set(`${a}\u001f${b}`, ri)
    max = Math.max(max, num(r.values[mi]))
  })
  const shade = (v: number) => {
    const step = max > 0 ? Math.min(4, Math.floor((v / max) * 5)) : 0
    return { background: ['#f4f8fc', '#dce8f4', '#b9d1ea', '#6f9fd2', '#3b6fa8'][step], color: step >= 3 ? '#fff' : undefined }
  }
  return (
    <div className="od-rep-table-wrap od-rep-pivot">
      <table className="table table-condensed od-rep-table">
        <thead>
          <tr>
            <th>
              {columnTitle(g0, locale)}
              {g1 ? ` / ${columnTitle(g1, locale)}` : ''}
            </th>
            {colsK.map((b) => (
              <th key={b} className="od-rep-num">
                {b}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowsK.map((a) => (
            <tr key={a}>
              <th scope="row">{a}</th>
              {colsK.map((b) => {
                const ri = cell.get(`${a}\u001f${b}`)
                if (ri === undefined) {
                  return (
                    <td key={b} className="od-rep-num od-rep-meta">
                      –
                    </td>
                  )
                }
                const raw = result.rows[ri]!.values[mi] ?? null
                return (
                  <td key={b} className={`od-rep-num${onPick ? ' od-rep-pick' : ''}`} style={shade(num(raw))} onClick={onPick ? () => onPick(ri) : undefined}>
                    {cellText(raw, measure, locale)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Indicatorul: un arc pe jumatate de cerc, cat s-a atins din tinta. */
export function Gauge({ value, target, better, column, locale }: { value: number; target: number; better: 'up' | 'down'; column: ResultColumn; locale: Locale }) {
  const reached = target === 0 ? 0 : value / target
  const good = better === 'down' ? value <= target : value >= target
  const f = Math.max(0, Math.min(1, reached))
  const r = 70
  const cx = 90
  const cy = 86
  const end = Math.PI * (1 - f)
  const arc = (to: number) => `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${(cx + r * Math.cos(to)).toFixed(2)} ${(cy - r * Math.sin(to)).toFixed(2)}`
  return (
    <svg className="od-rep-gauge" width="180" height="104" viewBox="0 0 180 104" role="img" aria-label={`${columnTitle(column, locale)}: ${Math.round(reached * 1000) / 10}%`}>
      <path d={arc(0)} fill="none" stroke="#e8ecf1" strokeWidth="16" />
      {f > 0 ? <path d={arc(end)} fill="none" stroke={good ? '#2f7a46' : '#b0412e'} strokeWidth="16" /> : null}
      <text x={cx} y={cy - 8} textAnchor="middle" className="od-rep-gauge-pct">
        {`${Math.round(reached * 1000) / 10}%`}
      </text>
    </svg>
  )
}
